// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Button, Table, Icon, Message, Loader, Input, Dropdown, Label, Pagination } from 'semantic-ui-react';
import { brandingApi, TenantBranding } from '../api/brandingApi';

const tierOptions = [
  { key: '', text: 'All Tiers', value: '' },
  { key: 'NONE', text: 'None (Free)', value: 'NONE' },
  { key: 'GREY_LABEL', text: 'Grey Label', value: 'GREY_LABEL' },
  { key: 'WHITE_LABEL', text: 'White Label', value: 'WHITE_LABEL' },
];

const pageSizeOptions = [
  { key: '10', text: '10', value: 10 },
  { key: '20', text: '20', value: 20 },
  { key: '50', text: '50', value: 50 },
];

function tierColor(tier: string): 'grey' | 'orange' | 'blue' {
  switch (tier) {
    case 'GREY_LABEL': return 'orange';
    case 'WHITE_LABEL': return 'blue';
    default: return 'grey';
  }
}

function tierLabel(tier: string): string {
  switch (tier) {
    case 'GREY_LABEL': return 'Grey Label';
    case 'WHITE_LABEL': return 'White Label';
    default: return 'None';
  }
}

function BrandingListPage() {
  const [items, setItems] = useState<TenantBranding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const navigate = useNavigate();

  const loadBranding = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await brandingApi.listAll({
        search: searchTerm || undefined,
        tier: selectedTier || undefined,
        page: currentPage,
        size: pageSize,
      });
      setItems(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load branding records');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedTier, currentPage, pageSize]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedTier]);

  return (
    <Container style={{ padding: '2rem' }}>
      <Header as='h1'>
        <Icon name='paint brush' />
        <Header.Content>
          Branding Management
          <Header.Subheader>Manage branding configuration for all brokerages</Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          icon='search'
          placeholder='Search by name or subdomain...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '300px' }}
        />
        <Dropdown
          selection
          options={tierOptions}
          value={selectedTier}
          onChange={(_, { value }) => setSelectedTier(value as string)}
          placeholder='Filter by tier'
          style={{ minWidth: '200px' }}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button primary onClick={() => navigate('/branding/new')}>
            <Icon name='plus' />
            New Branding
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader active inline='centered'>Loading branding records...</Loader>
      ) : (
        <>
          <Table striped celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Display Name</Table.HeaderCell>
                <Table.HeaderCell>Subdomain</Table.HeaderCell>
                <Table.HeaderCell>Branding Tier</Table.HeaderCell>
                <Table.HeaderCell>Logo</Table.HeaderCell>
                <Table.HeaderCell>Last Updated</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='6' textAlign='center'>
                    <Message info>No branding records found</Message>
                  </Table.Cell>
                </Table.Row>
              ) : (
                items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>{item.displayName || '-'}</Table.Cell>
                    <Table.Cell>{item.subdomain || '-'}</Table.Cell>
                    <Table.Cell>
                      <Label color={tierColor(item.brandingTier)}>
                        {tierLabel(item.brandingTier)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {item.logos?.primary ? (
                        <img
                          src={item.logos.primary}
                          alt='Logo'
                          style={{ maxWidth: '60px', maxHeight: '30px' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span style={{ color: '#999' }}>No logo</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString()
                        : item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      <Icon
                        name='edit'
                        link
                        color='blue'
                        title='Edit branding'
                        onClick={() => navigate(`/branding/${item.tenantId}/edit`)}
                        style={{ cursor: 'pointer' }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
            <span>
              Showing {items.length} of {totalElements} records
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>Page size:</span>
              <Dropdown
                compact
                selection
                options={pageSizeOptions}
                value={pageSize}
                onChange={(_, { value }) => {
                  setPageSize(value as number);
                  setCurrentPage(0);
                }}
              />
              {totalPages > 1 && (
                <Pagination
                  activePage={currentPage + 1}
                  totalPages={totalPages}
                  onPageChange={(_, { activePage }) => setCurrentPage((activePage as number) - 1)}
                  size='mini'
                />
              )}
            </div>
          </div>
        </>
      )}
    </Container>
  );
}

export default BrandingListPage;
